/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { Logger } from "./logger";
import * as util from "../helpers/util";
/**
 * Class that can be used as a marker for successful completion of a set of events
 * @private
 * @module readyMarker
 */
export class ReadyMarker {
  private _callbacks: ((err: any) => void)[];
  private _signals: number;
  private _error: any;
  private _logger: Logger;
  private _name: string;

  /**
   * Creates new marker
   * @param name Name of the marker, useful in logging
   * @param signalsToWait Number of signals(events) that needs to be signaled (completed) before the marker is set as completed
   */
  constructor(name: string, signalsToWait: number) {
    this._logger = Logger.Get("ReadyMarker [" + name + "]");
    this._logger.debug("Initializing ready marker for '" + name + "' with " + signalsToWait + " signals to wait");
    if (signalsToWait <= 0) {
      throw new Error("Invalid signal number. Should be > 0");
    }
    this._signals = signalsToWait;
    this._callbacks = [];
    this._name = name;
  }

  /**
   * Adds a new callback that will be notified when the events are completed (success) or some error happened
   */
  public setCallback(callback: (err: any) => void) {
    if (this.isSet()) {
      callback(undefined);
      return;
    } else if (this.isError()) {
      callback(this._error);
      return;
    }
    this._callbacks.push(callback);
  }

  /**
   * Signals for a completion of an event. If this is the last event success callbacks will be called
   */
  public signal(message?: string) {
    this._logger.debug("Signaled - " + message + " - signals left " + (this._signals - 1));
    this._signals--;
    if (this._signals < 0) {
      throw new Error("Error in ready marker '" + this._name + " - signals are " + this._signals);
    }

    if (this.isSet()) {
      this._callbacks.forEach((callback) => {
        callback(undefined);
      });
    }
  }

  /**
   * Signals for an error in some of the events. This will notify all callbacks that the marker has failed
   */
  public error(error: string) {
    this._error = error;
    this._callbacks.forEach((errorCallback) => {
      errorCallback(error);
    });
  }

  /**
   * Checks if the marker is set (completed)
   */
  public isSet() {
    if (this.isError()) {
      return false;
    }
    return this._signals === 0;
  }

  /**
   * Checks if the marker has failed (some of the events has failed)
   */
  public isError() {
    return !util.isUndefined(this._error);
  }

  /**
   * Returns the last reported error (undefined if no error)
   */
  public getError() {
    return this._error;
  }
}
